import {DomainStore} from 'shared/store';
import { observe, makeAutoObservable } from 'mobx';
import {endpoints, types} from 'main/tools';
import Employee from 'main/stores/employees/Employee';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import _ from 'lodash';

class EmployeeSelectorState {
  store = new DomainStore();
  onChange;

  employees;
  locations;
  departments;
  errors = {};

  selectedEmployees = [];
  defaultEmployees = [];
  previouslySelectedEmployees = [];
  selectionType = 'all_employees';
  noEmployeesOption = false;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      onChange: false
    });
  }

  receiveProps({onChange, selectedEmployees, noEmployeesOption}) {
    this.onChange = onChange;
    this.defaultEmployees = selectedEmployees || [];
    this.noEmployeesOption = noEmployeesOption;
  }

  async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS
    ]);

    this.employees = this.store.getAllOrdered(types.EMPLOYEE, Employee);
    this.locations = this.store.getAllOrdered(types.LOCATION, Location);
    this.departments = this.store.getAllOrdered(types.DEPARTMENT, Department);
    this.initSelectedEmployees();

    observe(this, 'uniqueSelectedEmployees', () => this.onChange(this.uniqueSelectedEmployees), true);
  }

  initSelectedEmployees() {
    if (this.defaultEmployees.length > 0) {
      const employeeIds = _.map(this.defaultEmployees, 'id');
      this.selectedEmployees = _.filter(this.employees, e => _.includes(employeeIds, e.id));
    } else if (!this.noEmployeesOption) {
      this.selectedEmployees = this.employees;
    }

    if (this.selectedEmployees.length === this.employees.length) {
      this.selectionType = 'all_employees';
    } else if (this.selectedEmployees.length === 0 && this.noEmployeesOption) {
      this.selectionType = 'no_employees';
    } else {
      this.selectionType = 'select_employees';
    }
  }

  changeSelectionType(selectionType) {
    this.selectionType = selectionType;
    switch (selectionType) {
      case 'all_employees':
        this.previouslySelectedEmployees = this.selectedEmployees;
        this.selectedEmployees = this.employees;
        break;
      case 'select_employees':
        this.selectedEmployees = this.previouslySelectedEmployees;
        break;
      case 'no_employees':
        this.previouslySelectedEmployees = this.selectedEmployees;
        this.selectedEmployees = [];
        break;
      default:
        throw new Error(`Unknown selection type ${selectionType}`);
    }
  }

  selectLocation(location) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForLocation(location)
    );
  }

  selectDepartment(department) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForDepartment(department)
    );
  }

  selectManager(manager) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForManager(manager)
    );
  }

  selectEmployee(employee) {
    this.selectedEmployees.push(employee);
  }

  removeEmployee(employee) {
    this.selectedEmployees = _.reject(this.selectedEmployees, { id: employee.id });
  }

  clearEmployees() {
    this.selectedEmployees = [];
  }

  get uniqueSelectedEmployees() {
    return _.uniqBy(this.selectedEmployees, 'id');
  }

  get managers() {
    const managerIds = _.chain(this.employees).map('managerId').uniq().value();
    return _.filter(this.employees, e => managerIds.includes(e.id));
  }

  employeesForLocation(location) {
    return _.filter(this.employees, { location: { id: location.id }});
  }

  employeesForDepartment(department) {
    return _.filter(this.employees, { department: { id: department.id }});
  }

  employeesForManager(manager) {
    return _.filter(this.employees, { managerId: manager.id });
  }
}

export default EmployeeSelectorState;
